import { useEffect, useState } from 'react';
import { IncompleteStepAlertDetection, WizardModal } from '@apply/components';
import { LoadPanel } from 'devextreme-react/load-panel';
import { useSteps } from './components/Steps/Steps';
import { StandardLoanSection } from './components/Steps/constants/standardLoanSection';
import { useStandardLoanStore } from './state/standardLoanStore';
import { StandardLoanModalMode, standardLoanModalTitles } from './constants/standardLoanModalMode';
import { StandardLoanSource, useStandardLoanQuery } from './queries/standardLoanQueries';
import { handleAdminError } from '../../../../common/functions/handleAdminError';

/** Which loan to open (see StandardLoanSource; give one of its ids), plus open/close. */
export interface StandardLoanModalProps extends StandardLoanSource {
    isOpen: boolean;
    onClose: () => void;
}

export const StandardLoanModal = ({
    isOpen,
    onClose,
    //loanId = 257, //TODO: REMOVE LOCAL TEMP HARNESS AFTER COMPLETION
    loanId,
    loanTypeId,
    masterNoteId,
    bClientId,
}: StandardLoanModalProps) => {
    const [currentStep, set_currentStep] = useState<number>(StandardLoanSection.SetupAndBorrower);

    // A fresh wizard on every open: the Popup keeps its content mounted while hidden, so without this the fields
    // would keep validation state from last time (e.g. "required" errors set when reset() cleared them after closing).
    // Bumped during render when isOpen turns true, so the new instance is what first shows (no flash of the old one).
    const [instanceKey, set_instanceKey] = useState(0);
    const [wasOpen, set_wasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
        set_wasOpen(isOpen);
        if (isOpen) {
            set_instanceKey((key) => key + 1);
        }
    }

    // A loanId edits that loan ("Update Loan"); anything else adds one ("Add Loan")
    const mode = loanId ? StandardLoanModalMode.Edit : StandardLoanModalMode.New;

    // instanceKey changes on every open, so each open loads its own copy of the loan, once
    const loanQuery = useStandardLoanQuery({ loanId, loanTypeId, masterNoteId, bClientId }, instanceKey, isOpen);
    const loadLoan = useStandardLoanStore((state) => state.loadLoan);

    useEffect(() => {
        if (loanQuery.data) {
            loadLoan(loanQuery.data, { loanId, loanTypeId, masterNoteId, bClientId });
        }
        // Only when a newly loaded loan arrives; the ids don't change while it's open
    }, [loanQuery.data]);

    useEffect(() => {
        if (loanQuery.error) {
            handleAdminError(loanQuery.error, 'An unexpected error has occurred. Please try again.');
        }
    }, [loanQuery.error]);

    // The form data lives in the standard loan store (not here), so the steps need nothing passed in.
    const steps = useSteps();

    // Every way out (Save and Exit, Finish, the X, Escape) comes through here.
    const close = () => {
        onClose();
    };

    // Start fresh next time, but only once the modal has finished hiding: resetting while it's still fading out
    // would visibly clear the fields (and flag required ones red) and jump back to the first step.
    const onHidden = () => {
        set_currentStep(StandardLoanSection.SetupAndBorrower);
        useStandardLoanStore.getState().reset();
    };

    return (
        <>
            <WizardModal
                key={instanceKey}
                id='standardLoanModal'
                isOpen={isOpen}
                onClose={close}
                onHidden={onHidden}
                title={standardLoanModalTitles[mode]}
                steps={steps}
                currentStep={currentStep}
                onStepChange={set_currentStep}
                stepNavigation='all'
                incompleteStepAlertDetection={IncompleteStepAlertDetection.All}
                onSaveAndExit={close}
                finishText='Finish'
                sidebarWidth={'230px'}
                height={'90vh'}
                // Shrinks with smaller screens, but stops at 1000px on large ones
                width={'90vw'}
                maxWidth={'1000px'}
                // Full-width content (e.g. an Expander's arrow) stays put when a long step starts to scroll
                reserveScrollbarSpace={true}
                // The Borrower step opens admin's EditEntityModal (a Material UI dialog), which must appear on top of this
                stackBelowDialogs={true}
            />
            {/* A DevExtreme overlay, so it opens above the wizard */}
            <LoadPanel
                visible={isOpen && loanQuery.isFetching}
                message='Loading...'
                hideOnOutsideClick={false}
            />
        </>
    );
};
